<template>
  <div
    class="tei-connection-card"
    :class="{
      [`tei-connection-card--${size}`]: true,
      'tei-connection-card--enabled': !disabled,
      'tei-connection-card--content': content,
      'tei-connection-card--resource-url': resourceUrl,
      'tei-connection-card--selected': selected,
      [`tei-connection-card--connection-point-${connectionPointPosition}`]: true,
      [`tei-connection-card--${state}`]: state,
      'tei-connection-card--view-more': viewMoreVisible,
    }"
  >
    <div
      :id="selectId"
      class="tei-connection-card__card-container"
      tabindex="-1"
      :aria-disabled="disabled ? 'true' : 'false'"
      @click="selectedChanged"
      @keyup.enter="selectedChanged"
    >
      <div
        v-if="content"
        class="tei-connection-card__content"
      >
        <TEIViewMoreContainer
          v-if="content"
          v-slot="props"
          class="tei-connection-card__view-more-container"
          button-align="center"
          :content="content"
          :allow-view-more="content && !resourceUrl"
          :max-lines="maxLines"
          :state="selected ? 'selected' : state"
          @view-more-clicked="onZoomClicked"
          @view-more-changed="viewMoreVisible = $event.viewMore"
        >
          <div
            class="tei-connection-card__content-text"
            aria-live="polite"
            :aria-label="ariaLabel"
          >
            {{ stripTags(props.content) }}
          </div>
        </TEIViewMoreContainer>
      </div>
      <div
        v-if="resourceUrl"
        class="tei-connection-card__resource"
        :style="{ backgroundImage: `url(${resourceUrl})` }"
      >
        <K5ZoomButton
          v-if="showZoomButton"
          class="tei-connection-card__zoom-button"
          @click.stop="onZoomClicked"
          @keyup.enter.stop
        />
      </div>
      <div
        v-if="connectionPointPosition"
        class="tei-connection-card__connection-point"
      />
      <TEIOptionStatusIcon
        v-if="state"
        class="tei-connection-card__state-icon"
        :status="state"
      />
    </div>
  </div>
</template>

<script>
import { mapActions } from 'pinia';
import { v4 as uuid } from 'uuid';
import K5ZoomButton from '@/components/ui-elements/K5ZoomButton.vue';
import TEIOptionStatusIcon from '@/components/teis/TEIOptionStatusIcon.vue';
import TEIViewMoreContainer from '@/components/teis/TEIViewMoreContainer.vue';
import { stripTags, extractTextFromHtml } from '@/lib/utils';
import {
  useModalStore,
} from '@/stores';
import * as types from '@/lib/constants/store';

export default {
  name: 'TEIConnectionCard',
  components: {
    K5ZoomButton,
    TEIOptionStatusIcon,
    TEIViewMoreContainer,
  },
  props: {
    id: {
      type: Number,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    resourceUrl: {
      type: String,
      default: null,
    },
    content: {
      type: String,
      default: null,
    },
    connectionPointPosition: {
      type: String,
      default: null, // top, bottom, left, right
    },
    selected: {
      type: Boolean,
      default: false,
    },
    state: {
      type: String,
      default: null, // correct, incorrect
    },
    size: {
      type: String,
      default: 'm', // xs, s, m
    },
    showZoomButton: {
      type: Boolean,
      default: true,
    },
    accessibilityContent: {
      type: String,
      default: null,
    },
    connectorLines: {
      type: Array,
      default: () => [],
    },
    rowOneOptions: {
      type: Array,
      default: () => [],
    },
    rowTwoOptions: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['selected-changed'],
  data() {
    return {
      selectId: uuid(),
      viewMoreVisible: false,
    };
  },
  computed: {
    ariaLabel() {
      const text = extractTextFromHtml(this.accessibilityContent || this.content);
      const connectorObj = this.connectorLines.find((connector) => (
        (this.connectionPointPosition === 'top' && connector.rowTwoId === this.id)
        || (this.connectionPointPosition === 'bottom' && connector.rowOneId === this.id)
      ));

      if (connectorObj) {
        const connectedTo = this.connectionPointPosition === 'top'
          ? extractTextFromHtml(this.rowOneOptions[connectorObj.rowOneId]?.text
          || this.rowOneOptions[connectorObj.rowOneId]?.accessibilityContent)
          : extractTextFromHtml(this.rowTwoOptions[connectorObj.rowTwoId]?.text
          || this.rowTwoOptions[connectorObj.rowTwoId]?.accessibilityContent);

        return this.$t('%(text)s is connected to %(connectedTo)s', { text, connectedTo });
      }
      return this.selected
        ? this.$t('%(text)s is selected', { text })
        : this.$t('%(text)s is not selected', { text });
    },
    maxLines() {
      if (this.size === 'xs') return 2;
      return this.resourceUrl ? 1 : 4;
    },
  },
  methods: {
    ...mapActions(useModalStore, [
      types.OPEN_MODAL,
    ]),
    stripTags,
    onZoomClicked() {
      this[types.OPEN_MODAL]({
        type: 'TEIConnectionCardModal',
        content: this.content,
        resourceUrl: this.resourceUrl,
      });
    },
    selectedChanged() {
      if (this.disabled) return;
      this.$emit('selected-changed', { id: this.id });
    },
  },
};
</script>

<style lang="stylus">
@import './styles/tei-fonts';

.tei-connection-card {
  $border-width = 5px;
  $border-2x = $border-width * 2;
  $border-half = $border-width / 2;
  $card-hover-distance = 6px;
  $card-inner-border-radius = 20px;
  $nebula-space-three-quarters = $nebula-space-1x * 0.75;

  $connection-point-size = $nebula-space-4x;
  $connection-point-half = $connection-point-size / 2;
  $connection-point-size-xs = $nebula-space-3x + $nebula-space-half;
  $connection-point-half-xs = $connection-point-size-xs / 2;

  $state-icon-size = $nebula-space-6x;
  $state-icon-half = $state-icon-size / 2;
  $state-icon-size-xs = $nebula-space-5x;
  $state-icon-half-xs = $state-icon-size-xs / 2;

  $content-height-xs = 68px;
  $content-height-s-no-resource = 146px;
  $content-height-s-with-resource = 48px;
  $resource-height-s-no-content = 146px;
  $resource-height-s-with-content = 98px;
  $content-height-m-no-resource = 146px;
  $content-height-m-with-resource = 48px;
  $resource-height-m-no-content = 146px;
  $resource-height-m-with-content = 108px;

  position: relative;
  width: 166px;

  &--xs {
    width: 296px;

    .tei-connection-card__content {
      height: $content-height-xs;
    }

    .tei-connection-card__connection-point {
      height: $connection-point-size-xs;
      width: $connection-point-size-xs;
    }

    .tei-connection-card__state-icon {
      height: $state-icon-size-xs;
      width: $state-icon-size-xs;
    }

    .tei-connection-card__content-text {
      tei-mikado-body-secondary-regular();
    }

    &.tei-connection-card--connection-point-right {
      .tei-connection-card__content {
        padding: $nebula-space-half $nebula-space-2x $nebula-space-half $nebula-space-1x;
      }
    }

    &.tei-connection-card--connection-point-left {
      .tei-connection-card__content {
        padding: $nebula-space-half $nebula-space-1x $nebula-space-half $nebula-space-2x;
      }
    }

    .tei-view-more-container {
      gap: 0;
    }
  }

  &--s {
    width: 160px;

    &.tei-connection-card--content {
      height: $content-height-s-no-resource + $border-2x;

      .tei-connection-card__content {
        height: $content-height-s-no-resource;
      }

      &.tei-connection-card--resource-url {
        height: $content-height-s-with-resource + $resource-height-s-with-content + $border-2x;

        .tei-connection-card__content {
          height: $content-height-s-with-resource;
        }

        .tei-connection-card__resource {
          height: $resource-height-s-with-content;
        }
      }

      &:not(.tei-connection-card--resource-url) {
        .tei-connection-card__content-text {
          tei-mikado-body-small-medium();
        }
      }
    }

    &.tei-connection-card--resource-url {
      height: $resource-height-s-no-content + $border-2x;

      .tei-connection-card__resource {
        height: 100%;
      }
    }
  }

  &--m {
    width: 200px;

    &.tei-connection-card--content {
      height: $content-height-m-no-resource + $border-2x;

      .tei-connection-card__content {
        height: $content-height-m-no-resource;
      }

      &.tei-connection-card--resource-url {
        height: $content-height-m-with-resource + $resource-height-m-with-content + $border-2x;

        .tei-connection-card__content {
          height: $content-height-m-with-resource;
        }

        .tei-connection-card__resource {
          height: $resource-height-m-with-content;
        }
      }

      &:not(.tei-connection-card--resource-url) {
        .tei-connection-card__content-text {
          tei-mikado-body-small-medium();
        }
      }
    }

    &.tei-connection-card--resource-url {
      height: $resource-height-m-no-content + $border-2x;

      .tei-connection-card__resource {
        height: 100%;
      }
    }
  }

  &--content {
    .tei-connection-card__resource {
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }
  }

  &--resource-url {
    .tei-connection-card__content {
      border-top-left-radius: 0;
      border-top-right-radius: 0;
    }
  }

  &--selected {
    .tei-connection-card__connection-point {
      background-color: var(--tei-button-primary-color);
      border-color: $nebula-color-white;
    }

    .tei-connection-card__card-container {
      background-color: var(--tei-card-selection-color);
    }
  }

  &--correct {
    .tei-connection-card__card-container {
      background-color: var(--tei-card-correct-color);
    }
  }

  &--incorrect {
    .tei-connection-card__card-container {
      background-color: var(--tei-card-incorrect-color);
    }
  }

  &--connection-point-left {
    .tei-connection-card__connection-point {
      left: -($connection-point-half-xs + $border-width);
      right: auto;
      top: ($content-height-xs / 2) - $connection-point-half-xs;
    }

    .tei-connection-card__state-icon {
      left: -($state-icon-half-xs + $border-width);
      right: auto;
      top: ($content-height-xs / 2) - $state-icon-half-xs;
    }

    &.tei-connection-card--enabled {
      .tei-connection-card__card-container {
        &:hover {
          transform: translateX(-($card-hover-distance));
        }
      }

      .tei-connection-card__card-container:focus-visible {
        transform: translateX(-($card-hover-distance));
      }
    }
  }

  &--connection-point-right {
    .tei-connection-card__connection-point {
      left: auto;
      right: -($connection-point-half-xs + $border-width);
      top: ($content-height-xs / 2) - $connection-point-half-xs;
    }

    .tei-connection-card__state-icon {
      left: auto;
      right: -($state-icon-half-xs + $border-width);
      top: ($content-height-xs / 2) - $state-icon-half-xs;
    }

    &.tei-connection-card--enabled {
      .tei-connection-card__card-container {
        &:hover {
          transform: translateX($card-hover-distance);
        }
      }

      .tei-connection-card__card-container:focus-visible {
        transform: translateX($card-hover-distance);
      }
    }
  }

  &--connection-point-top {
    .tei-connection-card__connection-point {
      top: -($connection-point-half + $border-width);
    }

    .tei-connection-card__state-icon {
      top: -($state-icon-half + $border-half);
    }

    &.tei-connection-card--enabled {
      .tei-connection-card__card-container {
        &:hover {
          transform: translateY($card-hover-distance);
        }
      }

      .tei-connection-card__card-container:focus-visible {
        transform: translateY($card-hover-distance);
      }
    }
  }

  &--connection-point-bottom {
    .tei-connection-card__connection-point {
      bottom: -($connection-point-half + $border-width);
    }

    .tei-connection-card__state-icon {
      bottom: -($state-icon-half + $border-half);
    }

    &.tei-connection-card--enabled {
      .tei-connection-card__card-container {
        &:hover {
          transform: translateY(-($card-hover-distance));
        }
      }

      .tei-connection-card__card-container:focus-visible {
        transform: translateY(-($card-hover-distance));
      }
    }

    &.tei-connection-card--content {
      &.tei-connection-card--resource-url {
        .tei-connection-card__content {
          padding-top: 0;
        }
      }
    }

    &.tei-connection-card--view-more {
      .tei-connection-card__content {
        padding-bottom: $nebula-space-2x;
      }
    }
  }

  &--view-more {
    .tei-connection-card__content-text {
      margin: 0;
    }
  }

  &--enabled {
    .tei-connection-card__card-container {
      cursor: pointer;
    }
  }

  &__input {
    opacity: 0;
    position: absolute;
    z-index: 0;
  }

  &__card-container {
    background-color: $nebula-color-white;
    border: $border-width solid $nebula-color-white;
    border-radius: $nebula-border-radius-large;
    box-shadow: var(--tei-connection-card-box-shadow);
    display: flex;
    flex-direction: column-reverse;
    height: 100%;
    position: relative;
    transition: $nebula-transition-default;
    width: 100%;
    z-index: 1;
  }

   &__content {
    border-radius: $card-inner-border-radius;
    display: flex;
    overflow: hidden;
    padding: $nebula-space-1x;
    text-align: center;
  }

  &__view-more-container {
    margin: auto;
  }

  &__content-text {
    tei-mikado-body-small-regular();
    text-align: center;
  }

  &__resource {
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
    border-radius: $card-inner-border-radius;
    position: relative;
    width: 100%;
  }

  &__zoom-button {
    bottom: 6px;
    inset-inline-end: 6px;
    position: absolute;
  }

  &__connection-point {
    background-color: $nebula-color-white;
    border: $border-width solid var(--tei-button-primary-color);
    border-radius: $connection-point-half;
    height: $connection-point-size;
    left: "calc(50% - %s)" % $connection-point-half;
    position: absolute;
    width: $connection-point-size;
    z-index: 0;
  }

  &__state-icon {
    left: "calc(50% - %s)" % $state-icon-half;
    position: absolute;
    z-index: 1;
  }
}
</style>
